import { ChatAgentToolName, ChatContextType } from '@/types/chat';
import {
  SuggestedChatPrompt,
  DEFAULT_SUGGESTED_CHAT_PROMPTS_BY_CONTEXT,
  DEFAULT_SUGGESTED_CHAT_PROMPTS_FOR_PAYSLIPS,
  SUGGESTED_CHAT_PROMPTS_FOR_ADP_PAYSLIPS,
  SUGGESTED_CHAT_PROMPTS_FOR_BANK_STATEMENTS,
  SUGGESTED_CHAT_PROMPTS_FOR_1040_TAX_FORMS,
  SUGGESTED_CHAT_PROMPTS_FOR_W2_TAX_FORMS,
  SUGGESTED_CHAT_PROMPTS_FOR_UTILITY_BILLS,
  SUGGESTED_CHAT_PROMPTS_FOR_CHECKS,
} from '@/constants/chat';

import { Document } from '@/types/document';
import {
  PAYSLIP,
  BANK_STATEMENT,
  UTILITY_BILL,
  CHECK,
} from '@/constants/documentTypes';
import {
  AUTOMATIC_DATA_PROCESSING,
  FORM_1040,
  W2,
} from '@/constants/documentSubtypes';

interface FormattedToolCall {
  title: string;
  fullDescription: string;
}

export function formatToolCall(
  toolName: ChatAgentToolName,
  args: string | Record<string, unknown>,
): FormattedToolCall {
  const parsedArgs: Record<string, unknown> =
    typeof args === 'string' ? JSON.parse(args) : args;

  switch (toolName) {
    case ChatAgentToolName.SearchByKeyword:
      return {
        title: 'Searching workspace data',
        fullDescription: `Searching workspace data for "${parsedArgs.keyword}"`,
      };
    case ChatAgentToolName.QueryDb:
      return {
        title: 'Querying workspace data',
        fullDescription: `Querying workspace data for "${parsedArgs.natural_language_query}"`,
      };
    case ChatAgentToolName.ReadCustomer:
      return {
        title: 'Reading customer',
        fullDescription: `Reading customer with UUID ${parsedArgs.customer_uuid}`,
      };
    case ChatAgentToolName.ReadDocument:
      return {
        title: 'Reading document',
        fullDescription: `Reading document with ID ${parsedArgs.document_id}`,
      };
    case ChatAgentToolName.Calculate:
      const formattedOperation = (parsedArgs.operation as string).toLowerCase();
      const formattedNumbers = (parsedArgs.numbers as Array<number>).join(', ');
      return {
        title: 'Using calculator',
        fullDescription: `Using calculator to ${formattedOperation} ${formattedNumbers}`,
      };
    case ChatAgentToolName.SearchKnowledgebase:
      return {
        title: 'Searching knowledgebase',
        fullDescription: `Searching knowledgebase for "${parsedArgs.search_phrase}"`,
      };
    case ChatAgentToolName.AnalyseTransactions:
      return {
        title: 'Analysing transactions',
        fullDescription: `Analysing transactions for document with ID ${parsedArgs.document_id}`,
      };
    case ChatAgentToolName.CheckSummarySection:
      const formattedAmounts = (
        parsedArgs.amounts as Array<{
          is_positive: boolean;
          value: number;
          type: string;
        }>
      )
        .map((amt) => `${amt.is_positive ? '' : '-'}${amt.value} (${amt.type})`)
        .join(', ');
      return {
        title: 'Checking summary section',
        fullDescription: `Checking summary section for document with ID ${parsedArgs.document_id} and amounts ${formattedAmounts}`,
      };
    case ChatAgentToolName.CompareNames:
      return {
        title: 'Comparing names',
        fullDescription: `Comparing names ${parsedArgs.name_one} and ${parsedArgs.name_two}`,
      };
    case ChatAgentToolName.AnalyseDocumentVisualElements:
      return {
        title: 'Analysing visual elements',
        fullDescription: `Analysing visual elements for document with ID ${parsedArgs.document_id}`,
      };
    case ChatAgentToolName.ValidateFinalResponse:
      return {
        title: 'Validating final response',
        fullDescription: 'Validating final response against risk policies',
      };
    default:
      return {
        title: 'Calling tool',
        fullDescription: `Calling tool ${toolName} with args ${JSON.stringify(parsedArgs)}`,
      };
  }
}

interface GetSuggestedPromptsArgs {
  chatContextType: ChatContextType;
  document?: Document;
}

const SUGGESTED_CHAT_PROMPTS_BY_DOCUMENT_SUBTYPE: Record<
  string,
  SuggestedChatPrompt[]
> = {
  [AUTOMATIC_DATA_PROCESSING]: SUGGESTED_CHAT_PROMPTS_FOR_ADP_PAYSLIPS,
  [FORM_1040]: SUGGESTED_CHAT_PROMPTS_FOR_1040_TAX_FORMS,
  [W2]: SUGGESTED_CHAT_PROMPTS_FOR_W2_TAX_FORMS,
};

const SUGGESTED_CHAT_PROMPTS_BY_DOCUMENT_TYPE: Record<
  string,
  SuggestedChatPrompt[]
> = {
  [BANK_STATEMENT]: SUGGESTED_CHAT_PROMPTS_FOR_BANK_STATEMENTS,
  [PAYSLIP]: DEFAULT_SUGGESTED_CHAT_PROMPTS_FOR_PAYSLIPS,
  [UTILITY_BILL]: SUGGESTED_CHAT_PROMPTS_FOR_UTILITY_BILLS,
  [CHECK]: SUGGESTED_CHAT_PROMPTS_FOR_CHECKS,
};

export function getSuggestedPrompts({
  chatContextType,
  document,
}: GetSuggestedPromptsArgs): SuggestedChatPrompt[] {
  if (chatContextType === ChatContextType.Document && document) {
    const { subtypes, document_type } = document;

    if (subtypes?.length > 0) {
      for (const subtype of subtypes) {
        if (SUGGESTED_CHAT_PROMPTS_BY_DOCUMENT_SUBTYPE[subtype]) {
          return SUGGESTED_CHAT_PROMPTS_BY_DOCUMENT_SUBTYPE[subtype];
        }
      }
    }

    if (
      document_type &&
      SUGGESTED_CHAT_PROMPTS_BY_DOCUMENT_TYPE[document_type]
    ) {
      return SUGGESTED_CHAT_PROMPTS_BY_DOCUMENT_TYPE[document_type];
    }
  }

  return DEFAULT_SUGGESTED_CHAT_PROMPTS_BY_CONTEXT[chatContextType];
}
